/**
 * Tracks data given for child components that fully enter the viewport
 *
 * @author Ryan Pape <rpape@wayfair.com>
 * @author Seraphina Orsini <sorsini@wayfair.com>
 * @copyright 2020 Wayfair LLC - All rights reserved
 */

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import {InView} from 'react-intersection-observer';

/**
 * Types of inview objects. Please use these constants to define type prop.
 */
export const BROWSE_PRODUCT_CARD = 'BrowseProductCard'; // Links to a PDP from a browse feed
export const CAROUSEL_ITEM = 'CarouselItem';
export const CONTENT_GATEWAY_ARTICLE_BLOCK = 'ContentGatewayArticleBlock'; // Display of products on the gateway page
export const HOMEPAGE_CARD = 'HomepageCard'; // Row of items on the homepage
export const PAGINATION_WIDGET = 'PaginationWidget'; // Allows user to change pages
export const SHOP_THE_LOOK_PHOTO = 'STLPhoto'; // Photo in STL feed
export const SHOP_THE_LOOK_RANDOM_CARD = 'STLRandomCard'; // Random content card in STL feed
export const SUBCATEGORY_CARD = 'SubcategoryCard'; // Links to a subcategory from a category page
export const B2B_PRICING_LABEL = 'B2BPricingLabel';
export const PRODUCT_BANNER_FLAG = 'ProductBannerFlag'; // Product Banner Flag in superbrowse and pdp
export const B2B_TOP5_DROPDOWN_BANNER = 'B2BTop5DropdownBanner'; // B2B Top 5 banner in header
export const ONSITEMEDIA_PLACEMENT = 'OnSiteMedia_Placement'; // Tracking for OSM placements/campaigns
export const CLICK_LOCATION = 'ClickLocation'; // Prefer use clickLocation prop instead
export const ATLAS_PLACEMENT = 'Atlas';
export const HOT_DEALS_BROWSE_GRID = 'HotDealsBrowseGrid'; // Browse product grid on Hot Deals page
export const BUNDLED_SAVINGS = 'BundledSavings';
export const B2B_LEAD_GEN_BROWSE_CARD = 'B2BLeadGenSuperBrowseCard'; // Tracking for B2B super browse lead gen card
export const HOT_DEALS_SIMPLIFIED_BROWSE_GRID = 'HotDealsSimplifiedBrowseGrid'; // Browse product grid on Hot Deals page

/**
 * Schemas map data prop keys to Scribe fields
 */
const DEFAULT_SCHEMA_FN = props => {
  const {data, clickLocation, threshold, positionNumber, contentId} = props;
  return {
    thresholds: threshold,
    positionNumbers: positionNumber,
    clickLocations: clickLocation,
    objects: data,
    contentIds: contentId,
  };
};
// Structured in-view types that have their own Scribe Fields
const SCHEMA_FNS = {
  [BROWSE_PRODUCT_CARD](props) {
    const {data, clickLocation, threshold, positionNumber, contentId} = props;
    const {sku, ...rest} = data;
    return {
      thresholds: threshold,
      positionNumbers: positionNumber,
      clickLocations: clickLocation,
      skus: sku,
      objects: rest,
      contentIds: contentId,
    };
  },
  [SHOP_THE_LOOK_PHOTO](props) {
    const {data, clickLocation, threshold, positionNumber, contentId} = props;
    const {id, type, ...rest} = data;
    return {
      thresholds: threshold,
      positionNumbers: positionNumber,
      clickLocations: clickLocation,
      photoIds: id,
      photoTypes: type,
      objects: rest,
      contentIds: contentId,
    };
  },
  [CLICK_LOCATION](props) {
    const {data, threshold, positionNumber, contentId} = props;
    const {clickLocation, ...rest} = data;
    return {
      thresholds: threshold,
      positionNumbers: positionNumber,
      clickLocations: props.clickLocation || clickLocation,
      objects: props.clickLocation ? data : rest,
      contentIds: contentId,
    };
  },
};

/**
 * Threshold for IntersectionObserver to observe child when it is 100% in the viewport
 */
const FULLY_IN_VIEWPORT = 1;

const trackInView = (trackingAPI, options) => {
  // Default to no-op if Scribe framework is not provided
  return trackingAPI?.pendingInView
    ? trackingAPI.pendingInView(options)
    : () => {};
};

const getScribeFields = type =>
  SCHEMA_FNS[type] ? SCHEMA_FNS[type] : DEFAULT_SCHEMA_FN;

export default class InViewTracking extends Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    type: PropTypes.string.isRequired, // Type of in-view item
    data: PropTypes.object, // Data to track when item comes into view
    clickLocation: PropTypes.string, // Click location of viewed item
    page: PropTypes.number, // Current page number of app, if any
    disabled: PropTypes.bool, // Do not count item as in-view
    threshold: PropTypes.number, // Track item as in-view when it passes this threshold (percentage inside the viewport)
    positionNumber: PropTypes.number, // Position of viewed item that STARTS AT 1,
    contentId: PropTypes.string, // Identifier specific to the content of in-view items
    tracker: PropTypes.object.isRequired,
  };

  static defaultProps = {
    data: {},
    clickLocation: null,
    page: null,
    disabled: false,
    threshold: FULLY_IN_VIEWPORT,
    positionNumber: 0, // positionNumbers start at 1, 0 indicates no position
    contentId: '',
  };

  /**
   * Allow item to be tracked again when data mutates
   */
  maybeReset = () => {
    const {data, type} = this.props;
    if (type !== this.prevType || !_.isEqual(data, this.prevData)) {
      this.isTracked = false;
      this.prevType = type;
      this.prevData = data;
    }
  };

  /**
   * Collects data for elements that are fully visible
   */
  handleObservation = isInView => {
    const {type, page} = this.props;
    if (!this.isTracked && isInView) {
      const fields = getScribeFields(type)(this.props);
      trackInView(this.props.tracker, {type, fields, page});
      this.isTracked = true;
    }
  };

  render() {
    this.maybeReset();
    return (
      <InView
        disabled={this.props.disabled || this.isTracked}
        threshold={this.props.threshold}
        onChange={this.handleObservation}
        data-enzyme-id="intersectionObserver"
      >
        {React.Children.only(this.props.children)}
      </InView>
    );
  }
}
